"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useAuth } from "../../../services/AuthContext"
import { api } from "../../../services/Axios"
import Loader from "../../utils/loader/Loader"
import MessageModal from "../../utils/avisos/MessageModal"
import NavBar from "../../utils/navbar/NavBar"
import PaymentMethodsModal from "./modal/PaymentMethodsModal"
import { FaStar, FaRocket, FaCrown, FaTimesCircle, FaCalendarAlt, FaCalendarCheck, FaChevronDown, FaWhatsapp } from "react-icons/fa"
import { useNavigate } from "react-router-dom"

const Subscription: React.FC = () => {
  const { userData, logout, syncAuthStatus } = useAuth()
  const [subscription, setSubscription] = useState<any>(userData?.subscription || null)
  const [serverDate, setServerDate] = useState<string | null>(null)  
  const [plans, setPlans] = useState<{
    id: string
    price: number
    name: string
    duration?: number
    details?: {
      productos: string
      cajeros: string
      sucursales: string
    }
  }[]>([])  
  const [isLoadingPlans, setIsLoadingPlans] = useState(true)
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true)
  const [message, setMessage] = useState("")
  const [messageType, setMessageType] = useState<"success" | "warning" | "error" | "info">("info")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)

  const INTEGRITY_SECRET = "prod_integrity_Mu7M7C55c1BLTmDzcRDGk1zOSy8wDiIW"

  const navigate = useNavigate()

  // Plan icons with tailwind classes
  const planIcons = {
    basic: (
      <div className="bg-[#2d53da] p-4 rounded-full">
        <FaStar className="text-white text-2xl" />
      </div>
    ),
    standard: (
      <div className="bg-[#2d53da] p-4 rounded-full">
        <FaRocket className="text-white text-2xl" />
      </div>
    ),
    premium: (
      <div className="bg-[#2d53da] p-4 rounded-full">
        <FaCrown className="text-white text-2xl" />
      </div>
    ),
  }

  useEffect(() => {
    if (subscription && serverDate) {
      const daysRemaining = getDaysRemaining(subscription.endDate, serverDate)
      if (daysRemaining <= 0 && subscription.status === "active") {
        setSubscription({ ...subscription, status: "inactive" })
        setMessage("Tu suscripción ha expirado. Selecciona un nuevo plan.")
        setMessageType("info")
        setIsModalOpen(true)
      }
    }
  }, [subscription, serverDate])

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get("/api/subscriptions/plans")
        setPlans(response.data)
      } catch (error) {
        console.error("Error al obtener planes:", error)
        setMessage("Error al obtener los planes de suscripción.")
        setMessageType("error")
        setIsModalOpen(true)
      } finally {
        setIsLoadingPlans(false)
      }
    }

    const fetchSubscription = async () => {
      try {
        const response = await api.get("/api/subscriptions")
        setSubscription(response.data.subscription)
        setServerDate(response.data.serverDate)
      } catch (error) {
        setMessage("Selecciona un plan de suscripción antes de continuar")
        setMessageType("success")
        setIsModalOpen(true)
      } finally {
        setIsLoadingSubscription(false)
      }
    }

    fetchPlans()
    fetchSubscription()
  }, [])

  const getDaysRemaining = (endDate: string, serverDate: string) => {
    const end = new Date(endDate).setHours(0, 0, 0, 0)
    const server = new Date(serverDate).setHours(0, 0, 0, 0)
    const timeDiff = end - server
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
  }

  if (isLoadingPlans) return <Loader />

  const daysRemaining = subscription && serverDate ? getDaysRemaining(subscription.endDate, serverDate) : 0

  const generateIntegrityHash = async (
    reference: string,
    amountInCents: number,
    currency: string,
    secret: string,
    expirationTime: string | null = null,
  ): Promise<string> => {
    let integrityString = `${reference}${amountInCents}${currency}`
    if (expirationTime) {
      integrityString += `${expirationTime}`
    }
    integrityString += `${secret}`

    const encoder = new TextEncoder()
    const data = encoder.encode(integrityString)
    const hashBuffer = await crypto.subtle.digest("SHA-256", data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")

    return hashHex
  }

  const handleSelectPlan = async (plan: { id: string; price: number; name: string }) => {
    const organizationId = userData?.organization?._id || "unknown"
    const reference = `${organizationId}_${plan.id}_${Date.now()}` // Genera referencia única
    const amountInCents = plan.price * 100
    const currency = "COP"
    const expirationTime = null

    await generateIntegrityHash(reference, amountInCents, currency, INTEGRITY_SECRET, expirationTime)

    // Llamamos al endpoint "manual" para crear/actualizar la suscripción con status "inactive"
    try {
      await api.post("/api/subscriptions/manual", {
        plan: plan.name,
      })
    } catch (error) {
      console.error("Error al crear la suscripción manual:", error)
    }

    // Mostramos el modal de consignación (para que el usuario vea las instrucciones)
    setIsPaymentModalOpen(true)
  }

  // Esta función se ejecuta cuando el usuario, tras ver el modal, hace clic en "Dirigirse"
  // Cuando la suscripción ya ha sido autorizada (por el administrador) y activada
  const handleDirect = async () => {
    await syncAuthStatus()
    setIsPaymentModalOpen(false)
    // Redirigimos al dashboard según el rol
    if (userData?.role === "vendedor") {
      navigate("/caja-venta")
    } else {
      navigate("/")
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar
        toggleSidebar={() => {}}
        toggleProfileMenu={() => setProfileMenuOpen(!profileMenuOpen)}
        toggleNotifications={() => {}}
        profileMenuOpen={profileMenuOpen}
        notificationsOpen={false}
        usuario={userData?.nombre || "Usuario"}
        rol={userData?.role || "Usuario"}
        orgName={userData?.organization?.name || "Sin Organización"}
        handleLogout={logout}
        showToggleMenu={false}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <MessageModal
          isOpen={isModalOpen}
          message={message}
          messageType={messageType}
          onClose={() => setIsModalOpen(false)}
        />

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Administrar Suscripción</h1>
          <div className="w-24 h-1 bg-[#2d53da] mx-auto rounded-full"></div>
        </div>

        {/* Current Subscription Card */}
        {!isLoadingSubscription && subscription ? (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
            <div className="bg-[#2d53da] p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-white mb-2">Tu Suscripción</h2>
              <p className="text-white/80">Detalles de tu plan actual y estado de suscripción</p>
            </div>

            <div className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center">
                {/* Days Remaining Circle */}
                <div className="mb-6 sm:mb-0 sm:mr-8">
                  <div
                    className={`w-32 h-32 rounded-full flex flex-col items-center justify-center border-4 ${
                      daysRemaining > 10
                        ? "border-green-500 text-green-600"
                        : daysRemaining > 3
                          ? "border-yellow-500 text-yellow-600"
                          : "border-red-500 text-red-600"
                    }`}
                  >
                    <span className="text-4xl font-bold">{daysRemaining}</span>
                    <span className="text-sm uppercase tracking-wider mt-1">Días</span>
                  </div>
                </div>

                {/* Subscription Details */}
                <div className="flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center text-gray-700 mb-1">
                        <FaCrown className="mr-2 text-[#2d53da]" />
                        <span className="font-medium">Plan:</span>
                      </div>
                      <p className="text-lg font-semibold ml-6">{subscription.plan}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center text-gray-700 mb-1">
                        <FaCalendarCheck className="mr-2 text-[#2d53da]" />
                        <span className="font-medium">Inicio:</span>
                      </div>
                      <p className="text-lg font-semibold ml-6">
                        {new Date(subscription.startDate).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center text-gray-700 mb-1">
                        <FaCalendarAlt className="mr-2 text-[#2d53da]" />
                        <span className="font-medium">Fin:</span>
                      </div>
                      <p className="text-lg font-semibold ml-6">
                        {new Date(subscription.endDate).toLocaleDateString()}
                      </p>
                    </div>

                    <div
                      className={`p-4 rounded-lg ${subscription.status === "inactive" ? "bg-red-50" : "bg-green-50"}`}
                    >
                      {subscription.status === "inactive" ? (
                        <div className="flex items-start">
                          <FaTimesCircle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                          <p className="text-red-700 text-sm">
                            Pago pendiente. Envíanos el comprobante para activar tu suscripción. 🙌
                          </p>
                          <a
                            href="https://wa.me/573502103574?text=Hola,%20he%20realizado%20el%20pago%20de%20mi%20suscripci%C3%B3n%20y%20quisiera%20solicitar%20su%20activaci%C3%B3n.%20%C2%A1Muchas%20gracias!"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full"
                          >
                            <button className="flex items-center justify-center w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl font-medium transition-colors">
                              <FaWhatsapp className="mr-2 text-xl" /> WhatsApp
                            </button>
                          </a>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <FaCrown className="text-green-500 mr-2" />
                          <p className="text-green-700 font-medium">Tu suscripción está activa.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // No Subscription Card
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
            <div className="bg-[#2d53da] p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-white mb-2">Sin Suscripción Activa</h2>
              <p className="text-white/80">Selecciona un plan para acceder a todas las funcionalidades</p>
            </div>

            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center">
              <div className="mb-6 sm:mb-0 sm:mr-8">
                <div className="w-32 h-32 rounded-full flex flex-col items-center justify-center border-4 border-gray-300 text-gray-400">
                  <span className="text-4xl font-bold">-</span>
                  <span className="text-sm uppercase tracking-wider mt-1">Suscripción</span>
                </div>
              </div>

              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl font-bold text-gray-800 mb-3">No tienes una suscripción registrada</h3>
                <p className="text-gray-600 mb-6">
                  Selecciona un plan para acceder a todas las funcionalidades de nuestra aplicación.
                </p>
                <button
                  onClick={() => document.getElementById("planes-disponibles")?.scrollIntoView({ behavior: "smooth" })}
                  className="inline-flex items-center px-6 py-3 bg-[#2d53da] text-white font-medium rounded-lg hover:bg-[#1e3eb8] transition-all shadow-md hover:shadow-lg"
                >
                  <FaStar className="mr-2" /> Ver Planes Disponibles
                  <FaChevronDown className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Available Plans */}
        {!isLoadingSubscription && (!subscription || subscription.status === "inactive") && (
          <div id="planes-disponibles" className="scroll-mt-24">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Planes Disponibles</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Elige el plan que mejor se adapte a las necesidades de tu negocio
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-xl"
                >
                  <div className="bg-[#2d53da] p-4 text-center">
                    <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                  </div>
                  <div className="p-6 text-center">
                    <div className="inline-block mb-4">{planIcons[plan.id as keyof typeof planIcons]}</div>
                    <div className="text-3xl font-bold text-[#2d53da] mb-4">
                      ${plan.price}
                      <span className="text-sm text-gray-500 font-normal">/mes</span>
                    </div>

                    <ul className="text-gray-600 mb-6 space-y-2">
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Productos: {plan.details?.productos}
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Cajeros: {plan.details?.cajeros}
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Sucursales: {plan.details?.sucursales}
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Módulo POS: Caja de ventas
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Notificaciones en tiempo real
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Estadísticas y reportes de ventas
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Carga masiva de inventario
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Acceso completo
                      </li>
                      <li className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Soporte incluido
                      </li>
                    </ul>

                    <button
                      onClick={() => handleSelectPlan(plan)}
                      className="w-full py-3 px-4 bg-[#2d53da] text-white font-medium rounded-lg hover:bg-[#1e3eb8] transition-all shadow-md hover:shadow-lg"
                    >
                      Seleccionar Plan
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Modal */}
        <PaymentMethodsModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          manualPaymentConfirmed={subscription && subscription.status === "active"}
          onDirect={handleDirect}
        />
      </div>
    </div>
  )
}

export default Subscription

